"use client";

import { getBrowserLanguageFlagLocalization } from "@/utils/languageFlags";
import type {
  DeviceLocalization,
  IpLocalization,
  LocalizationSignals,
} from "@/utils/localizationRegion";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const LocalizationContext = createContext<LocalizationSignals>({});

// Merges server-seeded signals (IP geo, Accept-Language) with device signals refined post-mount.
export default function LocalizationProvider({
  ipLocalization,
  initialDeviceLocalization = {},
  children,
}: {
  ipLocalization: IpLocalization;
  initialDeviceLocalization?: DeviceLocalization;
  children: ReactNode;
}) {
  const { ipCountry, ipTimeZone } = ipLocalization;
  const [deviceLocalization, setDeviceLocalization] =
    useState<DeviceLocalization>(initialDeviceLocalization);

  useEffect(() => {
    setDeviceLocalization(getBrowserLanguageFlagLocalization());
  }, []);

  const value = useMemo(
    () => ({ ipCountry, ipTimeZone, ...deviceLocalization }),
    [ipCountry, ipTimeZone, deviceLocalization]
  );

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

export const useLocalization = () => useContext(LocalizationContext);
